import React, { createContext, useState, useCallback } from "react";
import { transformUserPermissionArr } from "../components/common/CommonFunctions";

export const AuthContext = createContext("");

export const SessionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentProject, setCurrentProject] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('current_project') || 'null'); } catch { return null; }
  });

  // Define all keys used in storage
  const storageKeys = [
    "token",
    "is_super_admin",
    "is_admin",
    "userId",
    "profile",
    "employee_id",
    "user_name",
    "login_role",
    "permissions",
    "role_id",
    "role_name",
    "tenant_id",
    "tenant_name",
    "tenant_db",
    "product_type",   // 'trustedge' | 'engage' — drives which product UI to render
    "features",
    "product_type",
    // Not written at login — set by the release popup once it has shown. Listed
    // here so logout clears it, which is what makes the popup appear again on
    // the next sign-in rather than staying suppressed for the whole tab.
    "releases_popup_shown",
  ];

  /** --------------------------
   *  Helpers
   *  ------------------------- */
  const setStorage = (storage, data) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        storage.setItem(key, typeof value === "object" ? JSON.stringify(value) : value);
      }
    });
  };

  const removeStorage = (storage) => {
    storageKeys.forEach((key) => storage.removeItem(key));
  };

  const parsePermissions = (loginData) => {
    return loginData?.permissions?.permissions?.length
      ? transformUserPermissionArr([...loginData.permissions.permissions])
      : [];
  };

  /** --------------------------
   *  Login
   *  ------------------------- */
  const loginauth = async (loginData, keepSignedIn) => {
    try {
      const commonData = {
        token: loginData.token,
        is_super_admin: loginData.is_super_admin,
        is_admin: loginData.is_admin,
        userId: loginData.user_id,
        profile: loginData.profile,
        employee_id: loginData.employee_id,
        user_name: loginData.user_name,
        login_role: loginData.login_role,
        permissions: loginData.permissions,
        role_id: loginData.role_id,
        role_name: loginData.role_name,
        tenant_id: loginData.tenant_id,
        tenant_name: loginData.tenant_name,
        tenant_db: loginData.tenant_db,
        product_type: loginData.product_type || 'trustedge',   // default keeps legacy behaviour
        features: loginData.features,
        product_type: loginData.product_type || 'trustedge',
      };

      // Always save in sessionStorage
      setStorage(sessionStorage, commonData);

      // Optionally save in localStorage
      if (keepSignedIn) {
        const now = new Date();
        setStorage(localStorage, {
          ...commonData,
          keepSignedIn: true,
          keepSignedInExpiry: now.getTime() + 720 * 60 * 1000, // 12 hours
        });
      }

      setIsAuthenticated(true);
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  /** --------------------------
   *  Logout
   *  ------------------------- */
  const logoutauth = () => {
    removeStorage(sessionStorage);
    removeStorage(localStorage);
    localStorage.removeItem("keepSignedIn");
    localStorage.removeItem("keepSignedInExpiry");
    sessionStorage.removeItem("project_id");
    sessionStorage.removeItem("current_project");
    setIsAuthenticated(false);
    setCurrentProject(null);
  };

  const switchProject = useCallback((project) => {
    if (project) {
      sessionStorage.setItem("project_id", project.id);
      sessionStorage.setItem("current_project", JSON.stringify(project));
    } else {
      sessionStorage.removeItem("project_id");
      sessionStorage.removeItem("current_project");
    }
    setCurrentProject(project);
  }, []);

  /** --------------------------
   *  Sync sessionStorage with localStorage
   *  ------------------------- */
  const setSessionStorage = () => {
    const syncedData = {};
    storageKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) syncedData[key] = value;
    });
    setStorage(sessionStorage, syncedData);
  };

  /** --------------------------
   *  Expiry checker
   *  ------------------------- */
  const getWithExpiry = () => {
    const expiry = localStorage.getItem("keepSignedInExpiry");
    if (!expiry) return false;
    return new Date().getTime() <= Number(expiry);
  };

  /** --------------------------
   *  Check Authentication
   *  ------------------------- */
  const checkAuth = () => {
    if (localStorage.getItem("keepSignedIn")) {
      if (getWithExpiry()) {
        setSessionStorage();
        if (sessionStorage.getItem("token")) setIsAuthenticated(true);
      } else {
        logoutauth();
      }
    } else {
      if (sessionStorage.getItem("token")) setIsAuthenticated(true);
    }
  };

  /** --------------------------
   *  Helpers exposed
   *  ------------------------- */
  const loginServiceType = () => sessionStorage.getItem("is_admin");

  const hasPermission = (module, action = 'view') => {
    // Only super_admin bypasses. 'admin' used to be here as well, which meant
    // the UI showed an admin every menu while the API — which now only exempts
    // super_admin — refused the call behind it. Must match
    // FULL_ACCESS_ROLE_TYPES in platform_core/authentication/utils.py.
    const role = sessionStorage.getItem('login_role');
    if (role === 'super_admin') return true;
    try {
      const p = JSON.parse(sessionStorage.getItem('permissions') || '{}');
      return p?.[module]?.[action] ?? false;
    } catch {
      return false;
    }
  };

  /** Check if a feature is enabled for this tenant's plan */
  const hasFeature = (key) => {
    try {
      const f = JSON.parse(sessionStorage.getItem('features') || '{}');
      return f?.[key] ?? false;
    } catch {
      return false;
    }
  };

  /** Returns 'engage' | 'trustedge'. Default is 'trustedge' if not set. */
  const getProductType = () => sessionStorage.getItem('product_type') || 'trustedge';
  const isEngageTenant = () => getProductType() === 'engage';

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loginauth,
        logoutauth,
        checkAuth,
        loginServiceType,
        hasPermission,
        hasFeature,
        getProductType,
        isEngageTenant,
        currentProject,
        switchProject,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
